import { useEffect, useState } from "react";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import styled from "styled-components";
import { usePagination } from "src/hooks/usePagination";
import { PaginationProps } from "../ui/Pagination";
import { Table } from "src/components/ui";
import { Dispatch, FC, SetStateAction } from "react";

const BookingsContainer = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  margin: 3rem 0;
  border-radius: 16px;
  padding: 20px 0;

  .MuiTabs-root {
    min-height: auto;
    padding: 0 20px;
  }

  .MuiTabs-scroller {
    border-bottom: 1px solid ${(props) => props.theme.colors.gray_03};
  }
  .MuiButtonBase-root {
    border: none;
    cursor: pointer;
    font-size: 15px;
    background-color: transparent;
    text-transform: capitalize;
    color: ${(props) => props.theme.colors.text_01};
    height: fit-content;
    min-height: auto;
    padding: 10px;
    width: 110px;

    &:hover {
      color: ${(props) => props.theme.colors.text_02};
    }
  }

  .MuiTabs-root .Mui-selected {
    color: ${(props) => props.theme.colors.text_01};
  }

  .MuiTabs-indicator {
    background-color: ${(props) => props.theme.colors.text_01};
    height: 3px;
  }

  .MuiTabPanel-root {
    padding: 0;
  }

  .pd-20 {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50px;
    font-style: italic;
    font-weight: 600;
  }
`;

interface Props {
  rows: any[];
  BookingsTableHeaders: any[];
  title?: React.ReactNode;
  onRowClick?: (row: any) => void;
  allowRowClick?: boolean;
  searchParams?: URLSearchParams;
  setSearchParams?: (params: any) => void;
  paginationData: PaginationProps;
  pgn: number; // parent setter for current page
  setPgn: Dispatch<SetStateAction<number>>; // parent setter for current page
}

interface user {
  first_name: string;
  last_name: string;
  display_picture: string;
}

interface booking_info {
  user: user;
}

interface DisputeTypes {
  id: number;
  description: string;
  booking_id: string;
  createdAt: string;
  display_picture: string;
  status: string;
  booking_info: booking_info;
}

export const DisputeTabs: React.FC<Props> = ({
  rows,
  BookingsTableHeaders,
  title,
  onRowClick,
  allowRowClick = true,
  searchParams,
  setSearchParams,
  pgn,
  setPgn, // parent setter
  paginationData,
}) => {
  const [value, setValue] = useState("1");

  // Handle tab changes
  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    if (!setSearchParams) return;

    switch (newValue) {
      case "1":
        setSearchParams({ tabStatus: "all" });
        break;
      case "2":
        setSearchParams({ tabStatus: "pending" });
        break;
      case "3":
        setSearchParams({ tabStatus: "resolved" });
        break;
    }
  };

  // Sync tab value with searchParams
  useEffect(() => {
    if (!searchParams) return;

    const tabStatus = searchParams.get("tabStatus");
    if (tabStatus === "all") setValue("1");
    else if (tabStatus === "pending") setValue("2");
    else if (tabStatus === "resolved") setValue("3");
  }, [searchParams]);

  // Filter rows based on tab
  const tabData = (status: string) => {
    if (status !== "all") {
      return rows.filter((data) =>
        data.status?.toLowerCase().includes(status.toLowerCase() || "")
      );
    }
    return rows;
  };

  // Pagination hook
  const { page, limit, Pagination } = usePagination(
    {
      page: pgn,
      limit: 10,
      total: tabData(searchParams?.get("tabStatus") || "all").length,
      setPageNo: setPgn,
    },
    paginationData
  );

  // // Send current page to parent
  // useEffect(() => {
  //   console.log(currentPage);
  //   if (setParentPage) setParentPage(currentPage);
  // }, [currentPage, setParentPage]);

  // Paginate the rows
  // const paginatedRows = tabData(searchParams?.get("tabStatus") || "all").slice(
  //   (currentPage - 1) * limit,
  //   currentPage * limit
  // );

  const [paginatedRows, setPaginatedRows] = useState<DisputeTypes[]>([]);

  useEffect(() => {
    // setPaginatedRows(rows.slice((page - 1) * limit, page * limit));
    setPaginatedRows(tabData(searchParams?.get("tabStatus") || "all"));
  }, [page, limit, tabData(searchParams?.get("tabStatus") || "all")]);

  return (
    <BookingsContainer>
      <TabContext value={value}>
        <TabList onChange={handleChange} aria-label="dispute tabs">
          <Tab label="All" value="1" />
          <Tab label="Active" value="2" />
          <Tab label="Resolved" value="3" />
        </TabList>

        {["1", "2", "3"].map((tabVal, index) => (
          <TabPanel key={index} value={tabVal}>
            {paginatedRows.length > 0 ? (
              <>
                <Table
                  rows={paginatedRows}
                  headers={BookingsTableHeaders}
                  showHead={true}
                  allowRowClick={allowRowClick}
                  onRowClick={onRowClick}
                />
                <Pagination />
              </>
            ) : (
              <p className="pd-20">No Data Found</p>
            )}
          </TabPanel>
        ))}
      </TabContext>
    </BookingsContainer>
  );
};

export default DisputeTabs;
